/* Vareon: granular reveals and visible-only ambient motion; native browser APIs. */
(() => {
  if (window.__vareonMotionV2) return;
  window.__vareonMotionV2 = true;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const seen = new WeakSet();
  const observed = new Set();
  const active = new Map();
  const ambientTargets = new Map();
  const desktop = window.matchMedia('(min-width: 750px)');
  const cardSelector = '.product-card, .collection-card, .vareon-selection__card';
  let revealObserver;
  let ambientObserver;

  function cancelWithin(root) {
    for (const [target, animation] of active) {
      if (target === root || root.contains(target) || target.contains(root)) {
        animation.cancel();
        active.delete(target);
      }
    }
  }
  function updateAmbient() {
    for (const [target, visible] of ambientTargets) {
      const state = visible && !document.hidden && !reduced.matches ? 'running' : 'paused';
      if (target.classList.contains('hero__container')) target.dataset.vareonAmbient = state;
      else target.dataset.vareonEditorial = state;
    }
  }
  if ('IntersectionObserver' in window) {
    ambientObserver = new window.IntersectionObserver(entries => {
      for (const entry of entries) ambientTargets.set(entry.target, entry.isIntersecting);
      updateAmbient();
    });
    if (typeof document.documentElement.animate === 'function') {
      revealObserver = new window.IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting || reduced.matches) continue;
          const target = entry.target;
          revealObserver.unobserve(target);
          observed.delete(target);
          if (seen.has(target)) continue;
          seen.add(target);
          if (entry.boundingClientRect.top < -40 || target.contains(document.activeElement)) continue;
          const distance = target.matches(cardSelector) ? 18 : 12;
          const siblings = target.parentElement ? Array.from(target.parentElement.children) : [];
          const index = Math.max(0, siblings.indexOf(target)) % 4;
          const delay = target.matches(cardSelector) ? index * 65 : 0;
          try {
            const animation = target.animate([
              { opacity: .08, transform: 'translateY(' + distance + 'px)' },
              { opacity: 1, transform: 'translateY(0)' }
            ], {
              duration: desktop.matches ? 720 : 520,
              delay,
              easing: 'cubic-bezier(.2,.7,.2,1)',
              fill: 'backwards'
            });
            active.set(target, animation);
            animation.finished.then(() => active.delete(target), () => active.delete(target));
          } catch (_) { /* Content is never hidden when enhancements fail. */ }
        }
      }, { threshold: .03, rootMargin: '0px 0px -20px 0px' });
    }
  }
  function register() {
    const main = document.getElementById('MainContent');
    if (!main) return;
    const ambient = main.querySelectorAll('.hero__container, .media-with-content .media-block, .section-product-hotspots__content');
    for (const target of ambient) {
      target.querySelectorAll('.vareon-ambient-toggle').forEach(button => button.remove());
      if (ambientTargets.has(target)) continue;
      ambientTargets.set(target, true);
      if (ambientObserver) ambientObserver.observe(target);
    }
    updateAmbient();
    if (!revealObserver || reduced.matches) return;
    const targets = Array.from(main.querySelectorAll(
      cardSelector + ', .text-block, .vareon-selection__header, h1, h2, h3, .media-with-content__content'
    ));
    const footer = document.querySelector('.page-wrapper > footer');
    if (footer) targets.push(...footer.querySelectorAll('.group-block, .text-block, .logo-block'));
    for (const target of targets) {
      if (seen.has(target) || observed.has(target)) continue;
      if (target.closest('.hero__container')) continue;
      const card = target.closest(cardSelector);
      if (card && card !== target) continue;
      // Animate the smallest text unit, rather than both parent and child.
      if (!target.matches(cardSelector) && target.querySelector('.text-block, h1, h2, h3, .logo-block, .group-block')) continue;
      observed.add(target);
      revealObserver.observe(target);
    }
  }
  document.addEventListener('visibilitychange', updateAmbient);
  document.addEventListener('focusin', event => cancelWithin(event.target));
  document.addEventListener('shopify:section:select', event => cancelWithin(event.target));
  document.addEventListener('shopify:section:load', register);
  document.addEventListener('shopify:section:unload', event => {
    cancelWithin(event.target);
    for (const target of observed) if (event.target.contains(target)) {
      revealObserver.unobserve(target); observed.delete(target);
    }
    for (const target of ambientTargets.keys()) if (event.target.contains(target)) {
      if (ambientObserver) ambientObserver.unobserve(target);
      ambientTargets.delete(target);
    }
  });
  reduced.addEventListener('change', () => {
    updateAmbient();
    if (reduced.matches) {
      for (const animation of active.values()) animation.cancel();
      active.clear();
    } else register();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', register, { once: true });
  else register();
})();
